import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Plus,
  CircleNotch,
  User,
  Phone,
  Envelope,
  CheckCircle,
  XCircle,
  PencilSimple,
  Trash
} from '@phosphor-icons/react';


const ROLE_LABELS = {
  'admin': 'Administrador',
  'manager': 'Gerente',
  'sales': 'Vendedor',
  'technician': 'Técnico'
};

const ROLE_COLORS = {
  'admin': 'bg-purple-100 text-purple-800',
  'manager': 'bg-blue-100 text-blue-800',
  'sales': 'bg-green-100 text-green-800',
  'technician': 'bg-orange-100 text-orange-800'
};

export default function TechniciansPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: 'technician',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, {
          name: formData.name,
          phone: formData.phone,
          role: formData.role,
          is_active: formData.is_active
        }, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/users`, formData, { withCredentials: true });
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ email: '', password: '', name: '', phone: '', role: 'technician', is_active: true });
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name,
      phone: user.phone || '',
      role: user.role,
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API_URL}/api/users/${user.id}`, {
        is_active: !user.is_active
      }, { withCredentials: true });
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user:', error);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Eliminar al usuario "${user.name}"? Esta accion no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API_URL}/api/users/${user.id}`, { withCredentials: true });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="technicians-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Técnicos y Usuarios</h1>
          <p className="text-slate-500">Gestiona el equipo y sus permisos</p>
        </div>
        <button
          onClick={() => { setEditingUser(null); setFormData({ email: '', password: '', name: '', phone: '', role: 'technician', is_active: true }); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          data-testid="new-user-button"
        >
          <Plus size={20} weight="bold" />
          Nuevo Usuario
        </button>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-16">
            <CircleNotch size={32} className="animate-spin text-blue-600" />
          </div>
        ) : users.length > 0 ? (
          users.map((user) => (
            <div
              key={user.id || user.email}
              className={`bg-white rounded-xl border border-slate-200 p-5 ${
                !user.is_active ? 'opacity-60' : ''
              }`}
              data-testid={`user-card-${user.id || user.email}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                    user.is_active ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {user.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{user.name}</h3>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[user.role] || ROLE_COLORS['technician']}`}>
                      {ROLE_LABELS[user.role] || user.role}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(user)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Editar"
                >
                  <PencilSimple size={18} className="text-slate-400" />
                </button>
                <button
                  onClick={() => handleDeleteUser(user)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  title="Eliminar"
                  data-testid={`delete-user-${user.id}`}
                >
                  <Trash size={18} className="text-slate-400 hover:text-red-600" />
                </button>
              </div>

              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Envelope size={16} className="text-slate-400" />
                  <span>{user.email}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-slate-400" />
                    <span>{user.phone}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {user.is_active ? (
                    <CheckCircle size={18} className="text-green-500" weight="fill" />
                  ) : (
                    <XCircle size={18} className="text-slate-400" weight="fill" />
                  )}
                  <span className={`text-sm ${user.is_active ? 'text-green-600' : 'text-slate-400'}`}>
                    {user.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <button
                  onClick={() => handleToggleActive(user)}
                  className={`text-sm font-medium ${
                    user.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'
                  }`}
                >
                  {user.is_active ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white rounded-xl border border-slate-200 py-16 text-center">
            <User size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">No hay usuarios registrados</p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={18} />
              Agregar primer usuario
            </button>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </h3>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="form-label">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  required
                  disabled={!!editingUser}
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="form-label">Contraseña *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="form-input"
                    required={!editingUser}
                    minLength={6}
                  />
                </div>
              )}
              <div>
                <label className="form-label">Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Rol *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="form-input"
                >
                  <option value="admin">Administrador</option>
                  <option value="manager">Gerente</option>
                  <option value="sales">Vendedor</option>
                  <option value="technician">Técnico</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <label htmlFor="is_active" className="text-sm">Usuario activo</label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingUser(null); }}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving && <CircleNotch size={18} className="animate-spin" />}
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
